import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd, { articleSchema } from '../components/JsonLd';

const Example = ({ children }) => (
  <div className="sf-example">
    <div className="sf-example-label">Real-world example</div>
    <p>{children}</p>
  </div>
);

const MarketAnalysis = () => {
  return (
    <>
      <SEO
        title="Market Analysis - Fundamental & Technical Analysis Guide | StckFuture"
        description="Learn fundamental and technical analysis, economic indicators, sector rotation, sentiment analysis, and how to analyze stocks for investment decisions."
        canonical="https://www.stckfuture.com/market-analysis"
      />
      <JsonLd schema={articleSchema({
        title: 'Market Analysis - Fundamental & Technical Analysis Guide | StckFuture',
        description: 'Learn fundamental and technical analysis, economic indicators, sector rotation, and how to analyze stocks for investment decisions.',
        url: 'https://www.stckfuture.com/market-analysis',
        dateModified: '2026-06-13',
      })} />
      <div className="sf-content-page">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">

              <div className="sf-page-header">
                <span className="sf-eyebrow">Module 5</span>
                <h1>Market Analysis</h1>
                <p className="lead">Learn to evaluate companies and time your decisions — using fundamental analysis to find what to buy, and technical analysis to figure out when.</p>
              </div>

              {/* Fundamental Analysis */}
              <div className="sf-card">
                <h3>Fundamental analysis</h3>
                <p>Fundamental analysis evaluates a company's intrinsic value — what it's actually worth — by examining financial statements, competitive advantages, management quality, and economic environment.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Profitability ratios</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>EPS (Earnings Per Share)</strong> — net income ÷ outstanding shares. Look for consistent growth over time.</li>
                      <li className="mb-1"><strong>ROE (Return on Equity)</strong> — net income ÷ shareholder equity. 15–20% is generally considered strong.</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Valuation ratios</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>P/E ratio</strong> — share price ÷ EPS. Lower suggests better value, but must be compared to industry peers and growth rates.</li>
                      <li className="mb-1"><strong>P/B ratio</strong> — share price ÷ book value per share. Below 1.0 may indicate undervaluation.</li>
                    </ul>
                  </div>
                </div>
                <strong className="sf-text-small">Qualitative factors to consider</strong>
                <ul className="sf-text-small sf-text-muted mt-2 mb-3">
                  <li><strong>Competitive advantage</strong> — what does this company have that competitors can't easily copy?</li>
                  <li><strong>Management quality</strong> — track record, capital allocation decisions, strategic vision</li>
                  <li><strong>Industry trends</strong> — is the sector growing or shrinking? Is disruption a near-term risk?</li>
                  <li><strong>Regulatory environment</strong> — are there current or potential regulations that could impact the business?</li>
                </ul>
                <Example>In 2013, Netflix had a seemingly high P/E of 40 — which many dismissed as overvalued. Fundamental analysis looking deeper would have found 33% annual subscriber growth and expanding margins driven by the DVD-to-streaming transition. Those who invested based on that analysis saw ~2,000% gains over the next decade.</Example>
              </div>

              {/* Technical Analysis */}
              <div className="sf-card">
                <h3>Technical analysis</h3>
                <p>Technical analysis studies price charts and patterns to predict future price movements. It assumes that all known information is already reflected in the price — so the chart itself contains everything you need.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Trend indicators</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>Moving averages</strong> — average price over a set period (50-day, 200-day). A "golden cross" (short-term crossing above long-term) is considered bullish.</li>
                      <li className="mb-1"><strong>Trendlines</strong> — lines connecting price highs or lows to show direction and strength of trend.</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Momentum indicators</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>RSI (Relative Strength Index)</strong> — 0–100 scale. Above 70 = overbought (potential sell signal). Below 30 = oversold (potential buy signal).</li>
                      <li className="mb-1"><strong>MACD</strong> — shows relationship between two moving averages. Crossovers above the signal line are considered bullish.</li>
                    </ul>
                  </div>
                </div>
                <Example>In December 2018, Apple had fallen nearly 30%. Technical analysts noticed the RSI had dropped below 30 (oversold). In early January 2019, the MACD formed a bullish crossover. Investors who acted on those signals saw approximately 100% gains over the following year.</Example>
              </div>

              {/* Combining Both */}
              <div className="sf-card">
                <h3>Combining fundamental & technical analysis</h3>
                <p>Neither approach is complete on its own. The most practical approach for most investors:</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Fundamental first, technical second</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Use fundamentals to identify quality companies worth owning. Use technicals to find a good entry point — rather than buying immediately at any price.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Technical first, fundamental confirmation</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Use technicals to spot a breakout or signal. Then check the fundamentals to confirm the company is actually sound before committing capital.</p>
                  </div>
                </div>
                <Example>In March 2020, Microsoft fell ~30% despite having $130B+ in cash and dominance in cloud computing. Technical indicators showed the stock was oversold (RSI below 30). Investors combining those signals could have bought around $135 — a price that rose above $230 within a year.</Example>
              </div>

              {/* Sentiment Analysis */}
              <div className="sf-card">
                <h3>Sentiment analysis</h3>
                <p>Sentiment analysis tracks investor emotions and attitudes — often a contrarian signal. Extreme pessimism can mark bottoms; extreme optimism can mark tops.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Sentiment indicators</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>VIX (Fear Index)</strong> — measures expected volatility. Readings above 30 often indicate excessive fear and potential buying opportunities.</li>
                      <li className="mb-1"><strong>Put/Call ratio</strong> — high ratios signal bearish sentiment and can mark potential market bottoms.</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Modern sentiment sources</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>Social media</strong> — tracking mentions and sentiment on platforms like Reddit and Twitter</li>
                      <li className="mb-1"><strong>News sentiment</strong> — algorithmic analysis of financial news tone and volume</li>
                      <li className="mb-1"><strong>Investor surveys</strong> — AAII Investor Sentiment Survey, Investor Intelligence Newsletter</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Economic Indicators */}
              <div className="sf-card">
                <h3>Economic indicators</h3>
                <p>Economic indicators give context to market movements and help anticipate where we are in the economic cycle.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Leading indicators</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Signal future economic changes before they occur.</p>
                    <ul className="sf-text-small sf-text-muted mt-1">
                      <li>Purchasing Managers' Index (PMI)</li>
                      <li>Building permits</li>
                      <li>Yield curve shape</li>
                      <li>Consumer confidence surveys</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Lagging indicators</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Confirm trends already underway.</p>
                    <ul className="sf-text-small sf-text-muted mt-1">
                      <li>Unemployment rate</li>
                      <li>Consumer Price Index (CPI / inflation)</li>
                      <li>GDP growth rate</li>
                      <li>Corporate earnings</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Sector Rotation */}
              <div className="sf-card">
                <h3>Sector rotation</h3>
                <p>Different sectors of the economy tend to outperform at different stages of the economic cycle. Understanding this helps you position your portfolio ahead of shifts rather than chasing them.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Early cycle (recovery)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Consumer discretionary, financials, industrials tend to lead as confidence returns and rates are low.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Mid cycle (expansion)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Technology, communication services, materials benefit from strong growth and rising corporate profits.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Late cycle (peak)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Energy, healthcare, consumer staples hold up as growth slows and inflation rises.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Recession (contraction)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Utilities, consumer staples, healthcare — defensive sectors that provide essential goods and services regardless of economy.</p>
                  </div>
                </div>
              </div>

              {/* Market Breadth */}
              <div className="sf-card">
                <h3>Market breadth</h3>
                <p>Market breadth measures how many individual stocks are participating in a market move. A rally where only a handful of large-cap stocks rise while most stocks fall is weaker than it looks — and often a warning sign.</p>
                <ul>
                  <li className="mb-2"><strong>Advance-Decline Line</strong> — cumulative sum of advancing minus declining stocks. Divergence from the index price can signal an approaching reversal.</li>
                  <li className="mb-2"><strong>New highs vs. new lows</strong> — a healthy uptrend has expanding new highs; a narrowing number of new highs amid rising prices is a warning.</li>
                  <li className="mb-2"><strong>% above moving averages</strong> — below 20% often signals oversold conditions; above 80% often signals overbought.</li>
                </ul>
              </div>

              {/* Key Takeaways */}
              <div className="sf-takeaways">
                <h3>Key takeaways</h3>
                <ul>
                  <li>Fundamental analysis determines <em>what</em> to buy — by evaluating a company's true value</li>
                  <li>Technical analysis determines <em>when</em> to buy — by reading price patterns and momentum</li>
                  <li>Combining both approaches leads to more informed, better-timed investment decisions</li>
                  <li>Sentiment analysis is a contrarian tool — extreme fear is often a buying signal, extreme greed a warning</li>
                  <li>Economic indicators help you understand where we are in the cycle and position accordingly</li>
                  <li>Sector rotation means different parts of the market outperform at different cycle stages</li>
                  <li>Market breadth tells you whether a rally is broad and healthy or narrow and fragile</li>
                </ul>
              </div>

              <div className="sf-next-page">
                <Link to="/glossary" className="btn-sf-primary">
                  Next: Glossary →
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarketAnalysis;